import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Alert,
  AlertTitle,
  MenuItem,
  Select,
  FormControl,
  SelectChangeEvent,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../core/store';
import {
  BackendTimeSlot,
  DAYS_MAP,
  DAYS_REVERSE_MAP,
  TIME_SLOTS,
  TIME_SLOTS_REVERSE_MAP,
  UIDay,
  UITimeSlot,
} from '../../../core/constant/constant';
import { useScheduleChnageRequestMutation } from '../hooks/accountSettingsApi';
import { useApiErrors } from '../../enrollment/hooks/enrollmentHooks';
import { ErrorSource } from '../../../core/constant/model/api/api';
import ApiErrorLabel from '../../../core/components/ui/ApiErrorLabel';
import { clearApiError } from '../../../core/store/slices/apiError.slice';

type BackendDay = keyof typeof DAYS_MAP;

const DaySelectionButton: React.FC<{
  day: UIDay;
  selectedDays: UIDay[];
  toggleDay: () => void;
  disabled?: boolean;
}> = ({ day, selectedDays, toggleDay, disabled = false }) => {
  const isSelected = selectedDays.includes(day);

  return (
    <Button
      variant={isSelected ? 'contained' : 'outlined'}
      onClick={toggleDay}
      disabled={disabled}
      sx={{
        minWidth: '60px',
        color: isSelected ? 'white' : 'primary.main',
        borderColor: isSelected ? 'primary.main' : 'grey.300',
        bgcolor: isSelected ? 'primary.main' : 'transparent',
        '&:hover': {
          bgcolor: isSelected ? 'primary.dark' : 'rgba(99, 102, 241, 0.04)',
          borderColor: isSelected ? 'primary.dark' : 'primary.main',
        },
      }}
    >
      {day}
    </Button>
  );
};

const CoachingSchedule: React.FC = () => {
  const availability = useSelector((state: RootState) => state.availability);
  const memberId = useSelector((state: RootState) => state.basicInfo._id);
  const [showRequestSent, setShowRequestSent] = useState(false);
  const { hasApiError, getApiErrorBySource } = useApiErrors();
  const dispatch = useDispatch();

  const [selectedTimeSlot, setSelectedTimeSlot] = useState<BackendTimeSlot | ''>('');
  const [selectedDays, setSelectedDays] = useState<BackendDay[]>([]);

  const [currentTimeSlot, setCurrentTimeSlot] = useState<UITimeSlot | ''>('');
  const [currentDays, setCurrentDays] = useState<UIDay[]>([]);

  const scheduleMutation = useScheduleChnageRequestMutation();
  const isSaving = scheduleMutation.isLoading;

  useEffect(() => {
    if (availability) {
      if (availability.shiftTime && availability.shiftTime in TIME_SLOTS) {
        const displayTimeSlot = TIME_SLOTS[
          availability.shiftTime as keyof typeof TIME_SLOTS
        ] as UITimeSlot;
        setCurrentTimeSlot(displayTimeSlot);
      } else {
        setCurrentTimeSlot('');
      }

      const days: UIDay[] = (availability.shiftDays || [])
        .map((dayValue: string) => {
          if (dayValue in DAYS_MAP) {
            return DAYS_MAP[dayValue as keyof typeof DAYS_MAP] as UIDay;
          }
          return undefined;
        })
        .filter((day): day is UIDay => day !== undefined);

      setCurrentDays(days);

      setSelectedTimeSlot((availability.shiftTime as BackendTimeSlot) || '');
      setSelectedDays((availability.shiftDays || []) as BackendDay[]);
    }
  }, [availability]);

  const handleTimeSlotChange = (event: SelectChangeEvent<string>) => {
    setSelectedTimeSlot(event.target.value as BackendTimeSlot);
  };

  const handleDayToggle = (displayDay: UIDay) => {
    const backendDayValue = DAYS_REVERSE_MAP[displayDay] as BackendDay;

    setSelectedDays((prev) => {
      if (prev.includes(backendDayValue)) {
        return prev.filter((d) => d !== backendDayValue);
      } else {
        return [...prev, backendDayValue];
      }
    });
  };

  const coachingScheduleRequestError = hasApiError(ErrorSource.SCHEDULE_CHANGE_REQUEST)
    ? getApiErrorBySource(ErrorSource.SCHEDULE_CHANGE_REQUEST)
    : null;

  const handleSubmit = async () => {
    try {
      const result = await scheduleMutation.mutateAsync({
        memberId: memberId,
        newShiftTime: selectedTimeSlot,
        newShiftDays: selectedDays,
      });

      setShowRequestSent(true);

      setSelectedTimeSlot((availability.shiftTime as BackendTimeSlot) || '');
      setSelectedDays((availability.shiftDays || []) as BackendDay[]);
      
      setTimeout(() => {
        setShowRequestSent(false);
      }, 5000);

      return result;
    } catch (error) {
      console.error('Schedule change request failed:', error);
    }
  };

  const formatDaysForDisplay = (days: UIDay[]): string => {
    if (days.length === 0) return 'No days selected';

    return days.join(', ').replace(/,([^,]*)$/, ' and$1');
  };

  const handleDismissError = useCallback(() => {
    dispatch(clearApiError(ErrorSource.SCHEDULE_CHANGE_REQUEST));
  }, [dispatch]);

  const getSelectedUIdays = (): UIDay[] => {
    return selectedDays
      .map((backendDay) => {
        if (backendDay in DAYS_MAP) {
          return DAYS_MAP[backendDay] as UIDay;
        }
        return undefined;
      })
      .filter((day): day is UIDay => day !== undefined);
  };

  const hasChanges = (): boolean => {
    const currentTimeBackend = currentTimeSlot
      ? (TIME_SLOTS_REVERSE_MAP[currentTimeSlot] as BackendTimeSlot)
      : '';

    const currentDaysBackend = currentDays.map((day) => DAYS_REVERSE_MAP[day] as BackendDay);

    const sortedSelectedDays = [...selectedDays].sort();
    const sortedCurrentDays = [...currentDaysBackend].sort();

    return (
      selectedTimeSlot !== currentTimeBackend ||
      JSON.stringify(sortedSelectedDays) !== JSON.stringify(sortedCurrentDays)
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h5' fontWeight={600} sx={{ mb: 4 }}>
        Current Schedule
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <CalendarTodayOutlinedIcon sx={{ color: 'primary.main', mr: 2 }} />
        <Box>
          <Typography variant='h6'>{currentTimeSlot || 'No time selected'}</Typography>
          <Typography color='text.secondary'>{formatDaysForDisplay(currentDays)}</Typography>
        </Box>
      </Box>

      <Typography variant='h5' fontWeight={600} sx={{ mt: 6, mb: 4 }}>
        Request Schedule Change
      </Typography>

      <Stack spacing={4}>
        <Box>
          <Typography variant='subtitle1' fontWeight='medium' sx={{ mb: 2 }}>
            New Time Preference
          </Typography>

          <FormControl fullWidth>
            <Select
              value={selectedTimeSlot}
              onChange={handleTimeSlotChange}
              displayEmpty
              disabled={isSaving}
              sx={{ maxWidth: 400 }}
            >
              <MenuItem value='' disabled>
                Select a time slot
              </MenuItem>
              <MenuItem value='4'>9:30-11:30 AM ET</MenuItem>
              <MenuItem value='5'>12-2 PM ET</MenuItem>
              <MenuItem value='3'>2:30-4:30 PM ET</MenuItem>
              <MenuItem value='1'>5-7 PM ET</MenuItem>
              <MenuItem value='2'>7-9 PM ET</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box>
          <Typography variant='subtitle1' fontWeight='medium' sx={{ mb: 2 }}>
            New Days
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const).map((day) => {
              const uiDay = day as UIDay;
              return (
                <DaySelectionButton
                  key={day}
                  day={uiDay}
                  selectedDays={getSelectedUIdays()}
                  toggleDay={() => handleDayToggle(uiDay)}
                  disabled={isSaving}
                />
              );
            })}
          </Box>
        </Box>

        {showRequestSent && (
          <Alert severity='info' icon={<NotificationsOutlinedIcon />} sx={{ mt: 3 }}>
            <AlertTitle>Schedule Change Requested</AlertTitle>
            Schedule changes will take effect within 1-2 business days after approval.
          </Alert>
        )}

        {scheduleMutation.isError && (
          <Alert severity='error' sx={{ mt: 3 }}>
            <AlertTitle>Error</AlertTitle>
            {scheduleMutation.error?.message ||
              'Failed to submit schedule change request. Please try again.'}
          </Alert>
        )}

        {coachingScheduleRequestError && (
          <ApiErrorLabel
            message={coachingScheduleRequestError.message}
            variant='alert'
            testId='api-error'
            onDismiss={handleDismissError}
            dismissible={true}
          />
        )}

        <Box sx={{ mt: 2 }}>
          <Button
            variant='contained'
            color='primary'
            size='large'
            onClick={handleSubmit}
            disabled={isSaving || !hasChanges() || !selectedTimeSlot || selectedDays.length === 0}
            sx={{
              minWidth: 200,
              py: 1,
              minHeight: 50,
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}
          >
            {isSaving ? 'Submitting...' : 'Request Schedule Change'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default CoachingSchedule;
